import React, { useEffect, useState } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import {
  FiAlertCircle,
  FiCalendar,
  FiLock,
  FiMail,
  FiShield,
  FiUsers,
} from 'react-icons/fi';

import AuthLayout from '../components/auth/AuthLayout';
import TextField from '../components/auth/TextField';
import { useAuth } from '../context/AuthContext';

const EMPTY = { email: '', password: '' };

const ASIDE_POINTS = [
  {
    Icon: FiCalendar,
    title: 'Your schedule, in one place',
    text: 'Every upcoming session, who it is with, and how they booked.',
  },
  {
    Icon: FiUsers,
    title: 'Only your clients',
    text: 'You see the people booked with you and nobody else on the platform.',
  },
  {
    Icon: FiShield,
    title: 'Verified access',
    text: 'Portal accounts are issued after we verify your registration — they are never self-serve.',
  },
];

/**
 * A separate door into the same auth system.
 *
 * There is no clinician signup: accounts are provisioned after an application
 * is verified, because one grants access to real clients' booking data. This
 * page therefore only signs in — and refuses non-clinician accounts rather than
 * letting a customer in and stranding them on an empty portal.
 */
export default function ClinicianLoginPage() {
  const { login, logout, user, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const [values, setValues] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isAuthenticated && user?.role === 'clinician') {
      navigate('/clinician', { replace: true });
    }
  }, [isAuthenticated, user, navigate]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setValues((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => (prev[name] ? { ...prev, [name]: undefined } : prev));
    setFormError(null);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setFormError(null);

    const clientErrors = {};
    if (!values.email.trim()) clientErrors.email = 'Please enter your email address.';
    if (!values.password) clientErrors.password = 'Please enter your password.';
    if (Object.keys(clientErrors).length) {
      setErrors(clientErrors);
      return;
    }

    setSubmitting(true);
    try {
      const account = await login({
        email: values.email.trim(),
        password: values.password,
      });

      // Correct credentials, wrong kind of account. Sign back out rather than
      // leaving a customer holding a session they can't use here — and say
      // where they should go instead.
      if (account.role !== 'clinician') {
        await logout();
        setFormError(
          account.role === 'admin'
            ? 'That’s an admin account. Use the admin dashboard instead.'
            : 'That account isn’t registered as a clinician. If you’re looking for therapy, log in through the main site.'
        );
        return;
      }

      navigate('/clinician', { replace: true });
    } catch (err) {
      if (err.body?.errors) {
        setErrors(err.body.errors);
      } else {
        setFormError(
          err.status
            ? err.message
            : 'Couldn’t reach the server. Check your connection and try again.'
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout
      aside={
        <>
          <h2 className="mm-auth__aside-title">Clinician portal</h2>
          <p className="mm-auth__aside-text">
            Manage your MindlyFriend schedule, see who is booked in with you, and
            keep session records up to date.
          </p>

          <ul className="mm-auth__points">
            {ASIDE_POINTS.map(({ Icon, title, text }) => (
              <li className="mm-auth__point" key={title}>
                <Icon size={19} aria-hidden="true" />
                <span>
                  <strong>{title}</strong>
                  <span>{text}</span>
                </span>
              </li>
            ))}
          </ul>

          <p className="mm-auth__aside-note">
            Not listed with us yet? Clinician accounts are created once your
            registration and insurance have been verified &mdash;{' '}
            <Link to="/apply" style={{ color: '#fff', textDecoration: 'underline' }}>
              start an application
            </Link>
            .
          </p>
        </>
      }
    >
      <span className="mm-auth__eyebrow">For clinicians</span>
      <h1 className="mm-auth__title">Log in to the portal</h1>
      <p className="mm-auth__subtitle">
        Looking for therapy instead? <Link to="/login">Client log in</Link>.
      </p>

      <form className="mm-auth__form" onSubmit={handleSubmit} noValidate>
        {formError && (
          <div className="mm-form-alert mm-form-alert--error" role="alert">
            <FiAlertCircle size={16} aria-hidden="true" />
            <span>{formError}</span>
          </div>
        )}

        <TextField
          label="Work email address"
          name="email"
          type="email"
          icon={FiMail}
          placeholder="you@yourpractice.com"
          autoComplete="email"
          required
          value={values.email}
          onChange={handleChange}
          error={errors.email}
        />

        <TextField
          label="Password"
          name="password"
          type="password"
          icon={FiLock}
          placeholder="Your password"
          autoComplete="current-password"
          required
          value={values.password}
          onChange={handleChange}
          error={errors.password}
        />

        <div className="mm-form-row">
          <span className="mm-faint" style={{ fontSize: '0.85rem' }}>
            Accounts are issued by MindlyFriend
          </span>
          <Link to="/forgot-password">Forgot password?</Link>
        </div>

        <Button type="submit" className="mm-auth__submit" disabled={submitting}>
          {submitting ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" aria-hidden="true" />
              Logging in…
            </>
          ) : (
            'Log in to portal'
          )}
        </Button>
      </form>

      <div className="mm-auth__footer">
        Trouble signing in? <a href="#support">Contact clinician support</a>
      </div>
    </AuthLayout>
  );
}
